import { useQuery } from '@tanstack/react-query'

import api, { apiRequest } from '@/shared/api/client'

import {
  ATHLETE_VIEWER_FLAGS_KEY,
  FAN_GROWTH_KEY,
  type FanGrowthBucket,
  type FanGrowthPeriod,
  type ViewerFanFlags
} from '../lib/athleteProfile'

// BAT-1106: athlete profile reads (BAT-1099 endpoints, not in the generated
// client yet).

type ViewerFlagsResponse = {
  is_favorite?: boolean
  can_become_fan?: boolean
}

/**
 * The authenticated profile read carries the viewer flags; the anonymous
 * one (the page's main query) leaves them out. Absent flags — the favourite
 * lookup failed on the backend — hide the button rather than guess.
 */
export function useAthleteViewerFlags(clubId: string, viewerId?: string) {
  return useQuery<ViewerFanFlags>({
    // Keyed by the viewer: another account on this browser never sees the
    // previous one's «You're a fan».
    queryKey: [ATHLETE_VIEWER_FLAGS_KEY, clubId, viewerId],
    queryFn: async () => {
      const response = (await api.v2ClubsProfileMeDetail(
        clubId
      )) as ViewerFlagsResponse
      return {
        isFavorite: response?.is_favorite === true,
        canBecomeFan: response?.can_become_fan === true
      }
    },
    enabled: Boolean(clubId && viewerId),
    staleTime: 30_000
  })
}

type FanGrowthResponse = {
  period?: FanGrowthPeriod
  buckets?: Array<Partial<FanGrowthBucket>>
}

export function fetchFanGrowth(
  clubId: string,
  period: FanGrowthPeriod
): Promise<FanGrowthBucket[]> {
  return fetchFanGrowthAt(
    `/api/v2/clubs/${encodeURIComponent(clubId)}/fan-growth`,
    period
  )
}

export async function fetchFanGrowthAt(
  path: string,
  period: FanGrowthPeriod
): Promise<FanGrowthBucket[]> {
  const response = await apiRequest<FanGrowthResponse>({
    path,
    method: 'GET',
    query: { period },
    format: 'json'
  })
  return (response?.buckets ?? []).flatMap(bucket =>
    bucket.start
      ? [
          {
            start: bucket.start,
            joined: Math.max(0, Number(bucket.joined) || 0),
            left: Math.max(0, Number(bucket.left) || 0)
          }
        ]
      : []
  )
}

/** Fan growth of a club / athlete page: week = 7 days, month = 30 days,
 * year = 12 months, oldest first (BAT-1099). */
export function useFanGrowth(clubId: string, period: FanGrowthPeriod) {
  return useQuery<FanGrowthBucket[]>({
    queryKey: [FAN_GROWTH_KEY, clubId, period],
    queryFn: () => fetchFanGrowth(clubId, period),
    enabled: Boolean(clubId),
    staleTime: 60_000
  })
}
